import { INCOMPLETE_DEAL_MISSING_PREFIX, TARGET_BDM_ID_REQUIRED } from "@/lib/error-details";
import { CHANNEL_MANAGER } from "@/lib/role-labels";

/** A single FastAPI/Pydantic validation-error entry: `loc` ends in the
 * offending field name, `msg` is the human-readable reason. */
export interface PydanticError {
  loc?: unknown[];
  msg?: string;
}

export const GENERIC_422 = "Please check all fields are filled correctly.";

/** The decoded result of a 422 response: the actionable message AND the
 * identity of the offending field so the caller can highlight it. `field` is
 * the server field name (the `loc` leaf) when the error is field-scoped, or
 * `null` for a form-level error (a string `detail`, or no recoverable field). */
export interface Decoded422 {
  field: string | null;
  message: string;
}

/** Map a field name from a 422 `loc` to user-facing copy. Fields the wizard
 * no longer exposes (partner_org_name auto-seeds off-screen; target_bdm_id is
 * picked on the Assign step) get an actionable redirect, since the visible
 * form can't fix them. */
export function messageForField(field: string, msg: string | undefined): string {
  switch (field) {
    case "partner_org_name":
      return (
        "We couldn't register this deal because your saved organisation name " +
        "is invalid (it may be too long). This isn't a field on this form — " +
        `please update your organisation in your profile, or contact your ${CHANNEL_MANAGER}, then try again.`
      );
    case "target_bdm_id":
      return "Go back and choose who to assign this deal to before continuing.";
    case "client_name":
      return msg ? `Client name: ${msg}` : "Please check the client's name.";
    case "client_abn":
      return "ABN must be exactly 11 digits.";
    case "client_lead_contact":
      return msg ? `Client lead contact: ${msg}` : "Please check the client lead contact.";
    case "industry_vertical":
      return "Please choose a valid industry vertical.";
    case "client_domain":
      return msg ?? "Please check the end-customer domain.";
    case "service_offering":
      return "Please choose a valid service offering.";
    case "primary_use_case":
      return msg ? `Primary use case: ${msg}` : "Please check the primary use case.";
    case "total_contract_value":
      return msg ? `Total contract value: ${msg}` : "Please enter a valid total contract value.";
    case "proposed_term_months":
      return "Proposed term must be one of 12, 24, 36, 48 or 60 months.";
    case "target_close_date":
      return msg ? `Target close date: ${msg}` : "Please check the target close date.";
    case "estimated_gpu_count":
      return msg ? `Estimated GPU count: ${msg}` : "Please check the estimated GPU count.";
    case "infrastructure_notes":
      return msg ? `Infrastructure notes: ${msg}` : "Please check the infrastructure notes.";
    default:
      // A field we don't have bespoke copy for — surface the server's reason
      // verbatim if it gave one, otherwise the generic fallback.
      return msg ?? GENERIC_422;
  }
}

/** Hand-thrown server `detail` strings that are developer-worded (a field name
 * the user can't see) → the same actionable copy the field-level decoder
 * produces. CHP-458: the on-behalf guard (`routers/deals.py`) raises a bare
 * "target_bdm_id required when creating on behalf" string; with the wizard now
 * driving non-partners through the Assign-Deal-Owner step it shouldn't fire,
 * but if it ever does the user must see actionable copy, not the raw field. */
const SERVER_STRING_COPY: Record<string, string> = {
  [TARGET_BDM_ID_REQUIRED]: messageForField("target_bdm_id", undefined),
};

/** Turn a 422 `detail` into an actionable message AND the offending field.
 * `detail` is either a Pydantic error list (field validation) or a string (a
 * hand-thrown server check). The field is the `loc` leaf when present, so the
 * caller can set `aria-invalid` on the matching input; it is `null` for a
 * string `detail` or an unrecoverable shape (the message still falls back to
 * the blanket copy as a genuine last resort). */
/** CHP-474: the final-submit endpoint (`routers/deals.py::submit`) rejects an
 * incomplete draft with a hand-thrown string detail listing the missing fields,
 * e.g. `incomplete deal: missing service_offering, proposed_term_months` — NOT
 * a Pydantic field-error list. Without this, the string branch returns
 * `field=null`, so Step 3 shows the generic "Some required fields are missing"
 * with no field highlighted and no cross-step jump. Parse out the FIRST missing
 * field so `stepForField`/`onFieldError` can navigate to and highlight it. */
// Built from the shared prefix (CHP-1112) so the contract has one definition.
// trimEnd() + `\s+` preserves the original tolerance for any run of whitespace
// after the prefix; the prefix text is regex-safe (no metacharacters).
const INCOMPLETE_DEAL_RE = new RegExp(`^${INCOMPLETE_DEAL_MISSING_PREFIX.trimEnd()}\\s+(.+)$`, "i");
function fieldFromIncompleteDeal(detail: string): string | null {
  const m = INCOMPLETE_DEAL_RE.exec(detail);
  const list = m?.[1];
  if (!list) return null;
  // The list is comma-separated; take the first field name.
  const first = list.split(",")[0]?.trim();
  return first ? first : null;
}

export function decode422(detail: unknown): Decoded422 {
  if (typeof detail === "string" && detail.trim()) {
    const trimmed = detail.trim();
    const missingField = fieldFromIncompleteDeal(trimmed);
    if (missingField) {
      return { field: missingField, message: messageForField(missingField, undefined) };
    }
    return { field: null, message: SERVER_STRING_COPY[trimmed] ?? trimmed };
  }
  if (Array.isArray(detail)) {
    const first = detail.find((e): e is PydanticError => typeof e === "object" && e !== null);
    if (first) {
      const loc = Array.isArray(first.loc) ? first.loc : [];
      const field = loc.length > 0 ? String(loc[loc.length - 1]) : "";
      const msg = typeof first.msg === "string" ? first.msg : undefined;
      return { field: field || null, message: messageForField(field, msg) };
    }
  }
  return { field: null, message: GENERIC_422 };
}

/** Which wizard step owns a given server field — used to cross-step jump when
 * a submit on a later step fails validation on an earlier step's field.
 * `"client"` = Client details (Step 1), `"opportunity"` = Opportunity (Step 2).
 * Fields the form can't fix (partner_org_name, target_bdm_id) and unknown
 * fields return `null` (no jump — surface the message in place). */
export type DealFieldStep = "client" | "opportunity";

export function stepForField(field: string | null): DealFieldStep | null {
  switch (field) {
    case "client_name":
    case "client_abn":
    case "client_lead_contact":
    case "industry_vertical":
    case "client_domain":
      return "client";
    case "service_offering":
    case "primary_use_case":
    case "total_contract_value":
    case "proposed_term_months":
    case "target_close_date":
    case "estimated_gpu_count":
    case "infrastructure_notes":
      return "opportunity";
    default:
      return null;
  }
}

/** Review-r2 (cad3edb9): Client name reverted to a single field; the LEAD
 * CONTACT is now the First Name + Surname split. The server field is still
 * `client_lead_contact`, so highlighting maps a `client_lead_contact` 422 to
 * the lead-contact first-name input; every other field highlights its own. */
export function highlightFieldFor(serverField: string | null): string | null {
  if (serverField === "client_lead_contact") return "lead_first_name";
  return serverField;
}

/** Split a stored single name into first + surname for editing. Everything
 * after the first space is the surname (so "Mary Jane Watson" round-trips to
 * first="Mary", surname="Jane Watson"). */
export function splitName(name: string | null | undefined): {
  first: string;
  surname: string;
} {
  const value = (name ?? "").trim();
  if (!value) return { first: "", surname: "" };
  const idx = value.indexOf(" ");
  if (idx === -1) return { first: value, surname: "" };
  return { first: value.slice(0, idx), surname: value.slice(idx + 1).trim() };
}

/** Recombine first + surname into a single name string. Trims each part and the
 * join so a missing surname doesn't leave a trailing space. */
export function joinName(first: string, surname: string): string {
  return `${first.trim()} ${surname.trim()}`.trim();
}
